/**
 * SteemWorld HTTP Client
 *
 * A unified HTTP client for all SteemWorld API requests.
 * Handles error handling, query parameter building, and response parsing.
 */

import { STEEMWORLD_CONFIG } from './config'

/**
 * API Error class for SteemWorld specific errors
 */
export class SteemWorldApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public endpoint?: string
  ) {
    super(message)
    this.name = 'SteemWorldApiError'
  }
}

/**
 * Request options for API calls
 */
export interface RequestOptions {
  /** Query parameters to append to URL */
  params?: Record<string, string | number | boolean | undefined>
  /** Request timeout in milliseconds */
  timeout?: number
  /** AbortSignal for request cancellation */
  signal?: AbortSignal
}

/**
 * Builds URL with query parameters
 */
function buildUrl(
  baseUrl: string,
  endpoint: string,
  path: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  const url = new URL(`${endpoint}${path}`, baseUrl)

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value))
      }
    })
  }

  return url.toString()
}

/**
 * Creates an AbortController with timeout
 */
function createTimeoutController(
  timeout: number,
  externalSignal?: AbortSignal
): { controller: AbortController; cleanup: () => void } {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  // Handle external abort signal
  if (externalSignal) {
    externalSignal.addEventListener('abort', () => controller.abort())
  }

  return {
    controller,
    cleanup: () => clearTimeout(timeoutId),
  }
}

/**
 * Base HTTP client for SteemWorld API
 */
export async function request<T>(
  endpoint: string,
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const { params, timeout = STEEMWORLD_CONFIG.defaults.timeout, signal } = options

  const url = buildUrl(STEEMWORLD_CONFIG.baseUrl, endpoint, path, params)
  const { controller, cleanup } = createTimeoutController(timeout, signal)

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      signal: controller.signal,
    })

    if (!response.ok) {
      throw new SteemWorldApiError(
        `API request failed: ${response.statusText}`,
        response.status,
        `${endpoint}${path}`
      )
    }

    const data = await response.json()
    return data as T
  } catch (error) {
    if (error instanceof SteemWorldApiError) {
      throw error
    }

    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new SteemWorldApiError('Request timeout', undefined, `${endpoint}${path}`)
    }

    throw new SteemWorldApiError(
      error instanceof Error ? error.message : 'Unknown error occurred',
      undefined,
      `${endpoint}${path}`
    )
  } finally {
    cleanup()
  }
}

/**
 * Feeds API client
 */
export const feedsApi = {
  request: <T>(path: string, options?: RequestOptions) =>
    request<T>(STEEMWORLD_CONFIG.endpoints.feeds, path, options),
}

/**
 * Followers API client
 */
export const followersApi = {
  request: <T>(path: string, options?: RequestOptions) =>
    request<T>(STEEMWORLD_CONFIG.endpoints.followers, path, options),
}

/**
 * Notifications API client
 */
export const notificationsApi = {
  request: <T>(path: string, options?: RequestOptions) =>
    request<T>(STEEMWORLD_CONFIG.endpoints.notifications, path, options),
}

/**
 * Content Search API client
 */
export const contentSearchApi = {
  request: <T>(path: string, options?: RequestOptions) =>
    request<T>(STEEMWORLD_CONFIG.endpoints.contentSearch, path, options),
}
